import React, { useState } from 'react';
import { Box } from '@mui/material';
import { ChipsWrapper, StyledChip, StyledTextField } from './style/TagInput.style';

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Allow the same string to be added more than once. Defaults to false. */
  allowDuplicates?: boolean;
  'data-testid'?: string;
}

/**
 * Generic tag-style input. Operators type a string and confirm it with Enter or
 * comma, which adds it as a removable chip. No format validation is applied — use
 * this for free-form string arrays (e.g. registrant org identifiers, registrars).
 */
const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  allowDuplicates = false,
  'data-testid': dataTestId,
}) => {
  const [inputValue, setInputValue] = useState('');

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setInputValue('');
      return;
    }
    if (!allowDuplicates && value.includes(trimmed)) {
      setInputValue('');
      return;
    }
    onChange([...value, trimmed]);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handleDelete = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <StyledTextField
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addTag}
        fullWidth
        inputProps={{ 'data-testid': dataTestId }}
      />
      {value.length > 0 && (
        <ChipsWrapper sx={{ mt: 1 }}>
          {value.map((tag, index) => (
            <StyledChip
              key={`${tag}-${index}`}
              label={tag}
              onDelete={disabled ? undefined : () => handleDelete(index)}
            />
          ))}
        </ChipsWrapper>
      )}
    </Box>
  );
};

export default TagInput;
